import React from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { formatAmount, formatSignedAmount } from '../../utils';
import { showError, showInfo } from '../../actions/notificationActions';
import {
  REASON_MAX,
  isApprovalRequired,
  normaliseWallet,
  setOverdraftLimit,
  toFieldErrors,
  validateLimit,
  validateReason,
} from '../../services/overdraft.service';
import DataTable from '../DataTable';

// Raising or cutting a customer's credit line is a credit decision, so the form
// is deliberately two steps: enter the change, then confirm the delta.
class SetOverdraftLimitModal extends React.Component {
  state = {
    step: 'form', // 'form' -> 'confirm'
    limit: '',
    reason: '',
    errors: {},
    formError: null, // server message with no field to attach it to
    submitting: false,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.reset();
    }
  }

  reset = () => {
    const wallet = this.props.wallet || {};
    this.setState({
      step: 'form',
      limit: String(wallet.limit == null ? '' : wallet.limit),
      reason: '',
      errors: {},
      formError: null,
      submitting: false,
    });
  };

  close = () => {
    if (this.state.submitting) return;
    this.props.onClose();
  };

  setInput = (e) => {
    const { name, value } = e.target;
    this.setState((state) => ({
      [name]: value,
      errors: { ...state.errors, [name]: undefined },
      formError: null,
    }));
  };

  // The proposed limit as a number, or NaN when the field is not yet valid.
  nextLimit = () => {
    const raw = String(this.state.limit).trim();
    return raw === '' ? NaN : Number(raw);
  };

  currentLimit = () => {
    const wallet = this.props.wallet || {};
    return Number(wallet.limit) || 0;
  };

  hasChanged = () => {
    const next = this.nextLimit();
    return isFinite(next) && next !== this.currentLimit();
  };

  review = (e) => {
    e.preventDefault();

    const errors = {};
    const limitError = validateLimit(this.state.limit);
    const reasonError = validateReason(this.state.reason);
    if (limitError) errors.limit = limitError;
    if (reasonError) errors.reason = reasonError;

    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }

    this.setState({ step: 'confirm', errors: {}, formError: null });
  };

  backToForm = () => {
    if (this.state.submitting) return;
    this.setState({ step: 'form' });
  };

  submit = () => {
    const { userId, onSuccess, onClose, dispatch } = this.props;

    this.setState({ submitting: true });

    setOverdraftLimit(userId, {
      limit: this.nextLimit(),
      reason: this.state.reason,
    })
      .then((res) => {
        this.setState({ submitting: false });
        dispatch(showInfo(res.message || 'Overdraft limit updated.'));
        onClose();
        // A 2xx means the change was applied — a write parked for approval
        // arrives as a 403 in the catch below, never here. The response mirrors
        // `GET …/wallet` exactly, so hand the panel the new state instead of
        // making it re-fetch what we already have.
        onSuccess(normaliseWallet(res));
      })
      .catch((err) => {
        this.setState({ submitting: false });

        if (isApprovalRequired(err)) {
          // Close ours so the approval modal the interceptor opened is visible.
          onClose();
          dispatch(
            showInfo(
              err.message ||
                'This change is pending approval by a second authorised admin.'
            )
          );
          return;
        }

        if (err && err.status === 'validation-error') {
          // Back to the form so the operator can see and fix what was rejected.
          const fieldErrors = toFieldErrors(err.errors);
          this.setState({
            step: 'form',
            errors: fieldErrors,
            // Policy-ceiling and no-op rejections arrive as a bare message with
            // no `errors` array — surface them at form level.
            formError: Object.keys(fieldErrors).length ? null : err.message,
          });
          return;
        }

        dispatch(showError(err));
      });
  };

  renderForm() {
    const { limit, reason, errors, formError } = this.state;
    const wallet = this.props.wallet || {};
    const next = this.nextLimit();
    const willBeOverLimit =
      isFinite(next) && next < wallet.used && wallet.used > 0;

    return (
      <Form onSubmit={this.review}>
        <ModalBody>
          {formError ? <Alert color="danger">{formError}</Alert> : null}

          <p className="text-muted">
            Current limit <strong>{formatAmount(this.currentLimit())}</strong>
            {wallet.used > 0 ? (
              <React.Fragment>
                {' • '}currently owed{' '}
                <strong>{formatAmount(wallet.used)}</strong>
              </React.Fragment>
            ) : null}
          </p>

          <FormGroup>
            <Label for="overdraft-limit">New overdraft limit (₦)</Label>
            <Input
              type="number"
              name="limit"
              id="overdraft-limit"
              min="0"
              step="0.01"
              value={limit}
              onChange={this.setInput}
              // reactstrap 5.0.0-alpha.4 takes a tri-state `valid`, not the
              // `invalid` prop of later versions. Without `valid={false}` the
              // input never gets `.is-invalid` and Bootstrap keeps the sibling
              // `.invalid-feedback` at `display: none` — i.e. the error is
              // rendered but invisible.
              valid={errors.limit ? false : undefined}
            />
            <FormFeedback>{errors.limit}</FormFeedback>
            <FormText color="muted">
              Set <code>0</code> to remove the facility. The customer can spend
              down to minus this amount.
            </FormText>
          </FormGroup>

          <FormGroup>
            <Label for="overdraft-reason">Reason</Label>
            <Input
              type="textarea"
              name="reason"
              id="overdraft-reason"
              rows={3}
              maxLength={REASON_MAX}
              placeholder="e.g. Trusted merchant — approved by risk 2026-07-30"
              value={reason}
              onChange={this.setInput}
              valid={errors.reason ? false : undefined}
            />
            <FormFeedback>{errors.reason}</FormFeedback>
            <div className="d-flex justify-content-between">
              <small className="text-muted">
                Optional, but this is the audit record for the credit decision —
                please say why.
              </small>
              <small className="text-muted">
                {(reason || '').length}/{REASON_MAX}
              </small>
            </div>
          </FormGroup>

          {willBeOverLimit ? (
            <Alert color="danger" className="mb-0">
              This limit is below the {formatAmount(wallet.used)} already drawn.
              The customer will be over limit and blocked from spending until
              the debt is repaid — and the existing debt is <strong>not</strong>{' '}
              cancelled.
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.close}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={!this.hasChanged()}>
            Review change
          </Button>
        </ModalFooter>
      </Form>
    );
  }

  renderConfirm() {
    const { reason, submitting } = this.state;
    const wallet = this.props.wallet || {};
    const previous = this.currentLimit();
    const next = this.nextLimit();

    const removing = next === 0;
    const willBeOverLimit = next < wallet.used && wallet.used > 0;

    return (
      <React.Fragment>
        <ModalBody>
          <p>
            {removing
              ? 'Remove this customer’s overdraft facility?'
              : 'Confirm the new overdraft limit for this customer.'}
          </p>

          {/* No `borderless` — reactstrap 5.0.0-alpha.4's Table has no such
              prop, so it leaks to the DOM as an invalid attribute and warns. */}
          <DataTable card={false}>
            <tbody>
              <tr>
                <td className="text-muted">Limit</td>
                <td>
                  <strong>{formatAmount(previous)}</strong>
                  {' → '}
                  <strong>{formatAmount(next)}</strong>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Change</td>
                <td>
                  {next >= previous ? '+' : ''}
                  {formatSignedAmount(next - previous)}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Currently owed</td>
                <td>{formatAmount(wallet.used)}</td>
              </tr>
              <tr>
                <td className="text-muted">Reason</td>
                <td>
                  {reason.trim() ? (
                    reason.trim()
                  ) : (
                    <span className="text-muted">— none given —</span>
                  )}
                </td>
              </tr>
            </tbody>
          </DataTable>

          {removing ? (
            <Alert color="warning">
              The customer will no longer be able to spend past a zero balance.
              {wallet.used > 0 ? (
                <React.Fragment>
                  {' '}
                  The {formatAmount(wallet.used)} already owed is{' '}
                  <strong>not</strong> cancelled — it stays outstanding and is
                  repaid the next time the wallet is credited.
                </React.Fragment>
              ) : null}
            </Alert>
          ) : null}

          {willBeOverLimit && !removing ? (
            <Alert color="danger">
              The customer will be <strong>over limit</strong>: they owe{' '}
              {formatAmount(wallet.used)} against a new limit of{' '}
              {formatAmount(next)}. Further spending is blocked until the debt
              is repaid, and the existing debt is not cancelled.
            </Alert>
          ) : null}

          {!reason.trim() ? (
            <Alert color="warning" className="mb-0">
              No reason recorded. This is the only audit note attached to the
              change — go back and add one if you can.
            </Alert>
          ) : null}

          <small className="text-muted">
            This change may need approval from a second admin before it takes
            effect.
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={this.backToForm}
            disabled={submitting}
          >
            Back
          </Button>
          <Button
            color={removing || willBeOverLimit ? 'danger' : 'primary'}
            onClick={this.submit}
            disabled={submitting}
          >
            {submitting
              ? 'Please wait…'
              : removing
              ? 'Remove facility'
              : 'Set limit'}
          </Button>
        </ModalFooter>
      </React.Fragment>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { step } = this.state;

    return (
      <Modal isOpen={!!isOpen} toggle={this.close}>
        <ModalHeader toggle={this.close}>
          {step === 'confirm'
            ? 'Confirm overdraft limit'
            : 'Set overdraft limit'}
        </ModalHeader>
        {step === 'confirm' ? this.renderConfirm() : this.renderForm()}
      </Modal>
    );
  }
}

export default connect()(SetOverdraftLimitModal);
